import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Ban, Plus, X, Save, Loader2, RotateCcw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import NavigationMenu from '../components/NavigationMenu';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_EXTENSIONS = [
  '.exe', '.bat', '.cmd', '.com', '.scr', '.pif', '.msi', '.msp', '.mst',
  '.cpl', '.hta', '.inf', '.ins', '.isp', '.jse', '.lnk', '.reg', '.rgs',
  '.sct', '.shb', '.shs', '.vbe', '.vbs', '.wsc', '.wsf', '.wsh', '.ws'
];

const ExtensionsSettings = () => {
  const { token, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [extensions, setExtensions] = useState([]);
  const [newExt, setNewExt] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    if (!loading && !isAdmin) { navigate('/'); return; }
    if (isAdmin && token) loadExtensions();
  }, [isAdmin, loading, token]);

  const loadExtensions = async () => {
    try {
      const res = await axios.get(`${API}/admin/settings/extensions`, { headers: { Authorization: `Bearer ${token}` } });
      setExtensions(res.data.extensions || []);
    } catch { }
    setLoadingData(false);
  };

  const handleAdd = () => {
    let ext = newExt.trim().toLowerCase();
    if (!ext) return;
    if (!ext.startsWith('.')) ext = `.${ext}`;
    if (extensions.includes(ext)) {
      toast({ title: 'Extension déjà présente', variant: 'destructive' });
      return;
    }
    setExtensions(prev => [...prev, ext].sort());
    setNewExt('');
  };

  const handleRemove = (ext) => {
    setExtensions(prev => prev.filter(e => e !== ext));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/settings/extensions`, { extensions }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Extensions sauvegardées' });
    } catch {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
    setSaving(false);
  };

  const handleReset = () => {
    setExtensions([...DEFAULT_EXTENSIONS]);
    toast({ title: 'Liste réinitialisée aux valeurs par défaut' });
  };

  if (loading || loadingData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900">
      <NavigationMenu />
      <Toaster />
      <main className="pt-24 pb-16 max-w-3xl mx-auto px-6">
        <button onClick={() => navigate('/admin?tab=settings')} className="flex items-center gap-2 text-blue-300/60 hover:text-blue-300 mb-6 transition-colors" data-testid="back-to-admin">
          <ArrowLeft className="w-4 h-4" /> Retour à la configuration
        </button>

        <div className="bg-slate-800/50 border border-blue-500/20 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-red-500/20 rounded-lg">
              <Ban className="w-6 h-6 text-red-400" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Extensions bloquées</h1>
              <p className="text-sm text-blue-300/60">Les fichiers avec ces extensions seront refusés à l'upload</p>
            </div>
          </div>

          {/* Add extension */}
          <div className="flex items-center gap-2 mb-6">
            <input
              type="text"
              value={newExt}
              onChange={(e) => setNewExt(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
              placeholder="ex: .dll"
              className="flex-1 bg-slate-900/50 border border-blue-500/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/30 focus:outline-none focus:border-blue-400 text-sm"
              data-testid="new-extension-input"
            />
            <Button onClick={handleAdd} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-extension-btn">
              <Plus className="w-4 h-4 mr-1" /> Ajouter
            </Button>
          </div>

          {/* Extensions list */}
          <div className="flex flex-wrap gap-2 mb-6 min-h-[60px]" data-testid="extensions-list">
            {extensions.map((ext) => (
              <span key={ext} className="flex items-center gap-1.5 bg-red-500/10 border border-red-500/20 text-red-300 px-3 py-1.5 rounded-lg text-sm">
                {ext}
                <button onClick={() => handleRemove(ext)} className="text-red-400/60 hover:text-red-300 transition-colors" data-testid={`remove-ext-${ext}`}>
                  <X className="w-3.5 h-3.5" />
                </button>
              </span>
            ))}
            {extensions.length === 0 && (
              <p className="text-blue-300/40 text-sm py-2">Aucune extension bloquée — tous les types de fichiers seront acceptés</p>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 pt-4 border-t border-blue-500/10">
            <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-extensions-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Sauvegarder
            </Button>
            <Button variant="outline" onClick={handleReset} className="border-blue-500/20 text-blue-300/70 hover:text-white" data-testid="reset-extensions-btn">
              <RotateCcw className="w-4 h-4 mr-2" /> Réinitialiser
            </Button>
            <span className="text-xs text-blue-300/40 ml-auto">{extensions.length} extension{extensions.length > 1 ? 's' : ''} bloquée{extensions.length > 1 ? 's' : ''}</span>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ExtensionsSettings;
